"use client";
import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";
import { OfflineImage } from "../../shared/offline-image";

import {
  AlertCircle,
  Camera,
  Eye,
  Loader2,
  MapPin,
  MessageSquare,
  Package,
  Save,
  Shield,
  Trash2,
  Truck,
  Users,
  Warehouse,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import { useStableMemoComponent } from "../../../hooks/use-stable-memo-component";
import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { punchListSiteProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import { LoadingIndicator } from "../../ui/loading-indicator";
import {
  OptimizedCheckbox,
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "../shared/image-capture-picker";
import { LocalSectionImportControl } from "../shared/local-section-import-control";

interface SiteDataCDMFormProps extends AuditFormProps {
  siteId: string;
  templateId?: string;
  reportId: string;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  readOnly?: boolean;
  isViewingHistoricalVersion?: boolean;
  versionData?: any;
  previousCompletedReports?: any[];
}

interface SiteImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface SiteDataCDM {
  // Topography
  topography: string;
  topographyNotes: string;

  // Access to site
  siteAccess: string;
  siteAccessNotes: string;

  // Ground conditions
  groundConditions: string;
  groundConditionsNotes: string;

  // Boolean fields
  carParkAvailable: boolean;
  carParkAvailableNotes: string;

  storageAvailable: boolean;
  storageAvailableNotes: string;

  welfareFacilitiesAvailable: boolean;
  welfareFacilitiesAvailableNotes: string;

  // Safety fields (replaces single safetyAvailable)
  safetyInduction: boolean;
  safetyPPE: boolean;
  safetyEmergencyContact: boolean;
  safetyFirstAid: boolean;
  safetyFireExtinguisher: boolean;
  safetyRescuePlan: boolean;
  safetyWelfare: boolean;
  safetySignage: boolean;
  safetyFencing: boolean;
  safetyExclusionZones: boolean;
  safetyFallProtection: boolean;
  safetyPermitToWork: boolean;
  safetyNotes: string;

  // MVS Delivery fields (replaces single mvsDelivery)
  mvsDeveloper: boolean;
  mvsEPC: boolean;
  mvsOwner: boolean;
  mvsUtility: boolean;
  mvsThirdParty: boolean;
  mvsJoint: boolean;
  mvsNotDetermined: boolean;
  mvsDeliveryNotes: string;

  // Comments
  comments: string;

  // Images
  siteImages: SiteImage[];
}

const initialData: SiteDataCDM = {
  topography: "",
  topographyNotes: "",
  siteAccess: "",
  siteAccessNotes: "",
  groundConditions: "",
  groundConditionsNotes: "",
  carParkAvailable: false,
  carParkAvailableNotes: "",
  storageAvailable: false,
  storageAvailableNotes: "",
  welfareFacilitiesAvailable: false,
  welfareFacilitiesAvailableNotes: "",
  safetyInduction: false,
  safetyPPE: false,
  safetyEmergencyContact: false,
  safetyFirstAid: false,
  safetyFireExtinguisher: false,
  safetyRescuePlan: false,
  safetyWelfare: false,
  safetySignage: false,
  safetyFencing: false,
  safetyExclusionZones: false,
  safetyFallProtection: false,
  safetyPermitToWork: false,
  safetyNotes: "",
  mvsDeveloper: false,
  mvsEPC: false,
  mvsOwner: false,
  mvsUtility: false,
  mvsThirdParty: false,
  mvsJoint: false,
  mvsNotDetermined: false,
  mvsDeliveryNotes: "",
  comments: "",
  siteImages: [],
};

const topographyOptions = [
  { value: "flat", label: "Flat" },
  { value: "slope_ns", label: "Slope N-S" },
  { value: "slope_ew", label: "Slope E-W" },
  { value: "mixed", label: "Mixed Terrain" },
  { value: "rooftop", label: "Rooftop" },
  { value: "pitched", label: "Pitched" },
  { value: "barrel", label: "Barrel" },
  { value: "dome", label: "Dome" },
];

const accessTypeOptions = [
  { value: "road_access", label: "Road Access" },
  { value: "track_access", label: "Track/Unpaved Road" },
  { value: "field_access", label: "Field Access" },
  { value: "restricted_access", label: "Restricted Access" },
  { value: "pedestrian_only", label: "Pedestrian Only" },
  {
    value: "vehicle_full_visibility",
    label: "Vehicle Access with Full Visibility",
  },
  {
    value: "vehicle_limited_visibility",
    label: "Vehicle Access with Limited Visibility",
  },
  {
    value: "vehicle_with_marshal",
    label: "Vehicle Access with Traffic Marshal",
  },
];

const groundConditionOptions = [
  { value: "plain_ground", label: "Plain Ground" },
  { value: "undulated_ground", label: "Undulated Ground" },
  { value: "hilly_terrain", label: "Hilly Terrain" },
  { value: "rocky_ground", label: "Rocky Ground" },
  { value: "sandy_ground", label: "Sandy Ground" },
  { value: "clayey_ground", label: "Clayey Ground" },
  { value: "marshy_soft_ground", label: "Marshy / Soft Ground" },
];

export const SiteDataCDMForm = memo(function SiteDataCDMForm({
  siteId,
  templateId,
  reportId,
  sessionProgress,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: SiteDataCDMFormProps) {
  const { notify } = useNotification();

  const { loading } = useAppSelector((state) => state?.files);
  const [formData, setFormData] = useState<SiteDataCDM>(initialData);
  const formDataRef = useLatest(formData);
  const [selectedImage, setSelectedImage] = useState<SiteImage | null>(null);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);

  const [uploadingImages, setUploadingImages] = useState<{
    [key: string]: boolean;
  }>({});
  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  const dispatch = useAppDispatch();

  // Fetch existing files
  useEffect(() => {
    dispatch(
      fetchFiles({
        siteId,
        reportId,
      }),
    );
  }, [dispatch, siteId, reportId]);

  // Load version data when viewing historical version
  const currentSiteData = useVersionAwareSiteData(readOnly);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power).
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:site-data-cdm` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (
      JSON.stringify(currentSiteData?.siteData || {}) ===
      JSON.stringify(formData)
    ) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    // Update session progress
    const prog = punchListSiteProgress(formData);
    setSessionProgress((prev) => ({
      ...prev,
      [templateId]: { ...prev[templateId], "site-data": prog },
    }));
  }, [formData, setSessionProgress, templateId]);

  // Save handler
  const handleSave = useCallback(
    async (data: SiteDataCDM) => {
      try {
        if (!siteId || !reportId) {
          throw new Error("Site or report context is missing");
        }
        const mutationResult1 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: { reportId, siteData: data },
            }),
          ),
        );
        const config = notificationHelpers.formSaveSuccess("Site Data", [
          mutationResult1,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        console.error("Save failed:", error);
        const config = notificationHelpers.formSaveError(
          "Site Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [siteId, reportId, dispatch, notify],
  );

  // Debounced save
  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 7500,
    onSave: handleSave,
    onError: (error) => console.error("Save error:", error),
    readOnly,
  });

  useEffect(() => {
    if (currentSiteData) {
      const newFormData = {
        ...formDataRef.current,
        ...(currentSiteData?.siteData || {}),
      };
      resetFormBaseline(newFormData);
      setFormData(newFormData);
    }
  }, [currentSiteData, resetFormBaseline, formDataRef]);

  // Save handler for image descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult2 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult2],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for image descriptions (5000ms delay)
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving = isFormSaving || isImageSaving;
  const hasUnsavedChanges = hasFormUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    if (!lastSavedForm) return lastSavedImage;
    if (!lastSavedImage) return lastSavedForm;
    return lastSavedForm > lastSavedImage ? lastSavedForm : lastSavedImage;
  }, [lastSavedForm, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
  }, [forceFormSave, forceImageSave]);

  // Field change handler
  const handleFieldChange = useCallback((field: keyof SiteDataCDM) => {
    return (value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };
  }, []);

  // Image upload handler
  const handleImageUpload = useCallback(
    (files: FileList | File[] | null, filterData) => {
      if (!files) return;
      const fileArray = Array.from(files);

      const uploadKey = JSON.stringify(filterData);
      setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));

      requireMutationSuccess(
        dispatch(
          uploadMultipleFiles({
            files: fileArray,
            metadata: {
              siteId,
              entityType: "site-data-cdm",
              entityId: reportId,
              reportId,
            },
            filterData,
          }),
        ),
      )
        .then((mutationOutcome) => {
          fileArray.forEach((file) => {
            const config = notificationHelpers.imageUploadSuccess(
              file.name,
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          });
        })
        .catch((error) => {
          const config = notificationHelpers.imageUploadError(
            fileArray.length === 1
              ? fileArray[0].name
              : `${fileArray.length} files`,
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
        });
    },
    [siteId, reportId, dispatch, notify],
  );

  // Update image description
  const updateImageDescription = useCallback(
    (imageId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
    },
    [setImageDescriptions],
  );

  // Remove image handlers
  const handleRemoveImage = useCallback((imageId: string) => {
    setImageToDelete(imageId);
  }, []);

  const confirmRemoveImage = useCallback(() => {
    if (imageToDelete) {
      requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "Image",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "Image",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setImageToDelete(null);
        });
    }
  }, [imageToDelete, dispatch, notify]);

  const cancelRemoveImage = useCallback(() => {
    setImageToDelete(null);
  }, []);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // Get loading status
  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  // Check if uploading for a section
  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);
    return uploadingImages[uploadKey] || false;
  };

  // Availability Badge Component

  // Image Card Component
  const ImageCard = useStableMemoComponent(function ImageCard({
    image,
    imageDescription,
    onView,
    onRemove,
    onDescriptionUpdate,
    isDeleting,
  }: {
    image: SiteImage;
    imageDescription: string;
    onView: (image: SiteImage) => void;
    onRemove: (id: string) => void;
    onDescriptionUpdate: (id: string, description: string) => void;
    isDeleting: boolean | string;
  }) {
    return (
      <div key={image.id} className="relative group">
        <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative dark:bg-muted">
          <OfflineImage
            fileId={image?._id || image?.id || image?._clientId}
            src={image?.fileUrl || "/placeholder.svg"}
            alt="Site image"
            className="w-full h-full object-cover"
          />
          {loading && (isDeleting === true || isDeleting === image.id) && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
            onClick={() => onView(image)}
            disabled={loading}
            title="View Image"
          >
            <Eye className="w-3 h-3" />
          </Button>

          {!readOnly && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
              onClick={() => onRemove(image.id)}
              disabled={loading}
              title="Delete Image"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate truncate">
              {image?.originalName}
            </span>
            <Badge variant="outline" className="text-xs">
              {formatFileSize(image?.fileSize)}
            </Badge>
          </div>
          <OptimizedTextarea
            id={`desc-${image.id}`}
            label=""
            value={imageDescription}
            readOnly={readOnly}
            onChange={(value) => onDescriptionUpdate(image.id, value)}
            placeholder="Describe this site feature..."
            rows={2}
            disabled={loading}
          />
          <div className="text-xs text-slate-500 dark:text-muted-foreground">
            Date: {new Date(image.uploadDate).toLocaleDateString()}
          </div>
        </div>
      </div>
    );
  });

  // Image Section Component
  const ImageSection = useStableMemoComponent(function ImageSection({
    title,
    filterData,
    uploadFilterData,
    emptyMessage,
    buttonText = "Add Images",
  }: {
    title: string;
    filterData: any;
    uploadFilterData: any;
    emptyMessage: string;
    buttonText?: string;
  }) {
    const sectionImages = files?.filter(
      (file) =>
        file?.relatedTo?.entityType === "site-data-cdm" &&
        file?.relatedTo?.filterData &&
        JSON.stringify(file?.relatedTo?.filterData) ===
          JSON.stringify(filterData),
    );

    const isUploading = isUploadingForSection(uploadFilterData);

    return (
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">{title}</h4>
          {!readOnly && (
            <ImageCapturePicker
              buttonText={buttonText}
              isUploading={isUploading}
              disabled={loading}
              onConfirm={(selectedFiles) =>
                handleImageUpload(selectedFiles, uploadFilterData)
              }
            />
          )}
        </div>

        {isUploading && (
          <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
              <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
            </div>
          </div>
        )}

        {!isUploading && sectionImages.length === 0 ? (
          <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="flex items-center gap-3">
              <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                  No images added
                </p>
                <p className="text-xs text-slate-500 dark:text-muted-foreground">{emptyMessage}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sectionImages.map((image) => (
              <ImageCard
                key={image.id}
                image={image}
                imageDescription={
                  imageDescriptions[image.id] ?? image.fileDescription ?? ""
                }
                onView={setSelectedImage}
                onRemove={handleRemoveImage}
                onDescriptionUpdate={updateImageDescription}
                isDeleting={loading ? imageToDelete : false}
              />
            ))}
          </div>
        )}
      </div>
    );
  });

  return (
    <div className="space-y-4 md:space-y-6 relative">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="site-data-cdm"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          setFormData={setFormData}
        />
      )}

      {/* Floating save section */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
              bg-white/95 backdrop-blur-sm
              rounded-full border-2 border-gray-200/80
              shadow-lg
              w-12 h-12 sm:w-16 sm:h-16
              flex items-center justify-center
              group relative
              transition-all duration-300 ease-out
              transform hover:scale-110
              cursor-default
              p-2
            "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
                relative rounded-full w-12 h-12 sm:w-16 sm:h-16
                transition-all duration-300 ease-out
                flex flex-col items-center justify-center
                backdrop-blur-sm border-2
                shadow-lg hover:shadow-xl
                transform hover:scale-110 active:scale-95
                group
                ${
                  hasUnsavedChanges && !readOnly
                    ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
                    : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
                }
                ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
              `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Site Topography */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-blue-600" />
            <CardTitle>Site Topography</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <OptimizedSelect
            label="Topography Type"
            value={formData.topography}
            onChange={handleFieldChange("topography")}
            placeholder="Select topography type"
            options={topographyOptions}
            readOnly={readOnly}
            required
          />

          <OptimizedInput
            id="topographyNotes"
            label="Notes"
            value={formData.topographyNotes}
            onChange={handleFieldChange("topographyNotes")}
            placeholder="Notes for topography"
            readOnly={readOnly}
            required
          />

          <ImageSection
            title="Topography Images"
            filterData={{ topography: true }}
            uploadFilterData={{ topography: true }}
            emptyMessage="Add images to document site topography"
            buttonText="Add Topography Images"
          />
        </CardContent>
      </Card>

      {/* Site Access */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Truck className="w-6 h-6 text-green-600" />
            <CardTitle>Access to Site</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <OptimizedSelect
            label="Access Type"
            value={formData.siteAccess}
            onChange={handleFieldChange("siteAccess")}
            placeholder="Select access type"
            options={accessTypeOptions}
            readOnly={readOnly}
            required
          />
          <OptimizedInput
            id="siteAccessNotes"
            label="Notes"
            value={formData.siteAccessNotes}
            onChange={handleFieldChange("siteAccessNotes")}
            placeholder="Notes for site access"
            readOnly={readOnly}
            required
          />

          <ImageSection
            title="Site Access Images"
            filterData={{ site_access: true }}
            uploadFilterData={{ site_access: true }}
            emptyMessage="Add images to document site access"
            buttonText="Add Access Images"
          />
        </CardContent>
      </Card>

      {/* Ground Conditions */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-orange-600" />
            <CardTitle>Ground Conditions</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <OptimizedSelect
            label="Ground Condition Type"
            value={formData.groundConditions}
            onChange={handleFieldChange("groundConditions")}
            placeholder="Select ground condition"
            options={groundConditionOptions}
            readOnly={readOnly}
            required
          />

          <OptimizedInput
            id="groundConditionsNotes"
            label="Notes"
            value={formData.groundConditionsNotes}
            onChange={handleFieldChange("groundConditionsNotes")}
            placeholder="Notes for ground conditions"
            readOnly={readOnly}
            required
          />

          <ImageSection
            title="Ground Conditions Images"
            filterData={{ ground_conditions: true }}
            uploadFilterData={{ ground_conditions: true }}
            emptyMessage="Add images to document ground conditions"
            buttonText="Add Ground Images"
          />
        </CardContent>
      </Card>

      {/* Facilities Section */}
      <Card>
        <CardHeader>
          <CardTitle>Site Facilities & Services</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Car Park */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-slate-500 dark:text-muted-foreground" />
              <div>
                <h4 className="text-sm font-medium">Car Park</h4>
                <p className="text-xs text-slate-500 dark:text-muted-foreground">
                  Parking facilities availability
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <OptimizedCheckbox
                id="carParkAvailable"
                label="Car park available on site"
                checked={formData.carParkAvailable}
                onChange={handleFieldChange("carParkAvailable")}
                readOnly={readOnly}
                description="Check if adequate parking facilities are available"
              />
            </div>

            <OptimizedInput
              id="carParkAvailableNotes"
              label="Notes"
              value={formData.carParkAvailableNotes}
              onChange={handleFieldChange("carParkAvailableNotes")}
              placeholder="Notes for car park availability"
              readOnly={readOnly}
            />

            <ImageSection
              title="Car Park Images"
              filterData={{ car_park: true }}
              uploadFilterData={{ car_park: true }}
              emptyMessage="Add images to document parking facilities"
              buttonText="Add Parking Images"
            />
          </div>

          {/* Storage */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-3">
              <Warehouse className="w-5 h-5 text-slate-500 dark:text-muted-foreground" />
              <div>
                <h4 className="text-sm font-medium">Storage</h4>
                <p className="text-xs text-slate-500 dark:text-muted-foreground">
                  Storage facilities availability
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <OptimizedCheckbox
                id="storageAvailable"
                label="Storage facilities available"
                checked={formData.storageAvailable}
                onChange={handleFieldChange("storageAvailable")}
                readOnly={readOnly}
                description="Check if storage facilities are available for equipment and materials"
              />
            </div>

            <OptimizedInput
              id="storageAvailableNotes"
              label="Notes"
              value={formData.storageAvailableNotes}
              onChange={handleFieldChange("storageAvailableNotes")}
              placeholder="Notes for storage facilities"
              readOnly={readOnly}
            />

            <ImageSection
              title="Storage Facilities Images"
              filterData={{ storage: true }}
              uploadFilterData={{ storage: true }}
              emptyMessage="Add images to document storage facilities"
              buttonText="Add Storage Images"
            />
          </div>

          {/* Welfare Facilities */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-slate-500 dark:text-muted-foreground" />
              <div>
                <h4 className="text-sm font-medium">Welfare Facilities</h4>
                <p className="text-xs text-slate-500 dark:text-muted-foreground">
                  Worker welfare facilities
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <OptimizedCheckbox
                id="welfareFacilitiesAvailable"
                label="Welfare facilities available (toilets, rest area, etc.)"
                checked={formData.welfareFacilitiesAvailable}
                onChange={handleFieldChange("welfareFacilitiesAvailable")}
                readOnly={readOnly}
                description="Check if adequate welfare facilities are available for workers"
              />
            </div>

            <OptimizedInput
              id="welfareFacilitiesAvailableNotes"
              label="Notes"
              value={formData.welfareFacilitiesAvailableNotes}
              onChange={handleFieldChange("welfareFacilitiesAvailableNotes")}
              placeholder="Notes for welfare facilities"
              readOnly={readOnly}
            />

            <ImageSection
              title="Welfare Facilities Images"
              filterData={{ welfare: true }}
              uploadFilterData={{ welfare: true }}
              emptyMessage="Add images to document welfare facilities"
              buttonText="Add Welfare Images"
            />
          </div>
        </CardContent>
      </Card>

      {/* Safety Section - Enhanced with comprehensive checklist */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-red-600" />
            <CardTitle>Safety Measures & Equipment</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-muted-foreground">
            Comprehensive safety measures and equipment available on site
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg dark:bg-muted">
            <OptimizedCheckbox
              id="safetyInduction"
              label="Site Induction Completed"
              checked={formData.safetyInduction}
              onChange={handleFieldChange("safetyInduction")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyPPE"
              label="Personal Protective Equipment (PPE)"
              checked={formData.safetyPPE}
              onChange={handleFieldChange("safetyPPE")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyEmergencyContact"
              label="Emergency Contact Numbers Posted"
              checked={formData.safetyEmergencyContact}
              onChange={handleFieldChange("safetyEmergencyContact")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyFirstAid"
              label="First Aid Kit Available"
              checked={formData.safetyFirstAid}
              onChange={handleFieldChange("safetyFirstAid")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyFireExtinguisher"
              label="Fire Extinguisher Available"
              checked={formData.safetyFireExtinguisher}
              onChange={handleFieldChange("safetyFireExtinguisher")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyRescuePlan"
              label="Rescue/Emergency Plan in Place"
              checked={formData.safetyRescuePlan}
              onChange={handleFieldChange("safetyRescuePlan")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyWelfare"
              label="Welfare Facilities Available"
              checked={formData.safetyWelfare}
              onChange={handleFieldChange("safetyWelfare")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetySignage"
              label="Safety Signage Posted"
              checked={formData.safetySignage}
              onChange={handleFieldChange("safetySignage")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyFencing"
              label="Site Fencing/Security in Place"
              checked={formData.safetyFencing}
              onChange={handleFieldChange("safetyFencing")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyExclusionZones"
              label="Exclusion Zones Established"
              checked={formData.safetyExclusionZones}
              onChange={handleFieldChange("safetyExclusionZones")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyFallProtection"
              label="Fall Protection Measures"
              checked={formData.safetyFallProtection}
              onChange={handleFieldChange("safetyFallProtection")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="safetyPermitToWork"
              label="Permit to Work System in Place"
              checked={formData.safetyPermitToWork}
              onChange={handleFieldChange("safetyPermitToWork")}
              readOnly={readOnly}
            />
          </div>

          <OptimizedInput
            id="safetyNotes"
            label="Safety Notes"
            value={formData.safetyNotes}
            onChange={handleFieldChange("safetyNotes")}
            placeholder="Additional safety observations, issues, or comments..."
            readOnly={readOnly}
            className="mt-2"
          />

          <ImageSection
            title="Safety Measures Images"
            filterData={{ safety: true }}
            uploadFilterData={{ safety: true }}
            emptyMessage="Add images to document safety measures"
            buttonText="Add Safety Images"
          />
        </CardContent>
      </Card>

      {/* Delivery of MVS - Updated with checkbox group */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-purple-600" />
            <div>
              <CardTitle>
                Delivery of MVS (Inverter Transformer) Station
              </CardTitle>
              <p className="text-xs text-slate-500 dark:text-muted-foreground">
                Select all parties responsible for MVS station delivery
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg dark:bg-muted">
            <OptimizedCheckbox
              id="mvsDeveloper"
              label="Project Developer"
              checked={formData.mvsDeveloper}
              onChange={handleFieldChange("mvsDeveloper")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="mvsEPC"
              label="EPC Contractor"
              checked={formData.mvsEPC}
              onChange={handleFieldChange("mvsEPC")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="mvsOwner"
              label="Owner/Asset Owner"
              checked={formData.mvsOwner}
              onChange={handleFieldChange("mvsOwner")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="mvsUtility"
              label="Utility Company"
              checked={formData.mvsUtility}
              onChange={handleFieldChange("mvsUtility")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="mvsThirdParty"
              label="Third Party Contractor"
              checked={formData.mvsThirdParty}
              onChange={handleFieldChange("mvsThirdParty")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="mvsJoint"
              label="Joint Delivery"
              checked={formData.mvsJoint}
              onChange={handleFieldChange("mvsJoint")}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="mvsNotDetermined"
              label="Not Yet Determined"
              checked={formData.mvsNotDetermined}
              onChange={handleFieldChange("mvsNotDetermined")}
              readOnly={readOnly}
            />
          </div>

          <OptimizedInput
            id="mvsDeliveryNotes"
            label="Delivery Notes"
            value={formData.mvsDeliveryNotes}
            onChange={handleFieldChange("mvsDeliveryNotes")}
            placeholder="Additional details about MVS station delivery responsibilities..."
            readOnly={readOnly}
          />

          <ImageSection
            title="MVS Station Images"
            filterData={{ mvs_station: true }}
            uploadFilterData={{ mvs_station: true }}
            emptyMessage="Add images to document MVS station delivery"
            buttonText="Add MVS Images"
          />
        </CardContent>
      </Card>

      {/* General Comments */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-slate-600 dark:text-muted-foreground" />
            <CardTitle>Additional Comments & Observations</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <OptimizedTextarea
            label="General Site Comments"
            value={formData.comments}
            onChange={handleFieldChange("comments")}
            placeholder="Enter any additional site observations, comments, or special considerations..."
            rows={6}
            readOnly={readOnly}
          />

          <ImageSection
            title="General Site Images"
            filterData={{ general_site: true }}
            uploadFilterData={{ general_site: true }}
            emptyMessage="Add general site images"
            buttonText="Add General Images"
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Delete Image</h3>
            </div>
            <p className="text-slate-600 dark:text-muted-foreground mb-6">
              Are you sure you want to delete this image? This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={cancelRemoveImage}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRemoveImage}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
                disabled={loading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={
                  selectedImage?._id ||
                  selectedImage?.id ||
                  selectedImage?._clientId
                }
                src={selectedImage?.fileUrl || "/placeholder.svg"}
                alt={selectedImage.originalName}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                {new Date(selectedImage.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress footer */}
      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Form Status:
            </span>
            <Badge variant="default">Complete</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Progress:
            </span>
            <Badge variant="outline">
              {sessionProgress?.[templateId]?.["site-data"] ||
                punchListSiteProgress(formData)}
              %
            </Badge>
          </div>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
